import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { Rating, Loader } from '@lomarengas/design-system';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import DemoImage1 from '../../assets/img/demoimages/rovarakka01.jpeg';
import DemoImage2 from '../../assets/img/demoimages/rovarakka02.jpeg';
import DemoImage3 from '../../assets/img/demoimages/rovarakka03.jpeg';
import {
  AccommodationPublicDetailsResponse,
  AttributeData,
  DistanceData,
} from '../../common/api/searchService';
import {
  AccommodationPublicDetailsParams,
  getAccommodationPublicData,
  selectAccommodationPublicDataLoadingStatus,
  selectAccommodationPublicDataResult,
} from '../searchBookableAccommodations/searchBookableAccommodationsSlice';
import { selectFeedbackItemsLoadingStatus } from '../feedbacks/FeedbacksSlice';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import { useLocale } from '../../locales/useLocale';

export interface AgentAccommodationViewProps {
  AccommodationSlug: string;
}
export type Amenity = {
  icon: string;
  text: string;
};
const AgentAccommodationView = ({
  AccommodationSlug,
}: AgentAccommodationViewProps) => {
  const { t } = useTranslation(['common', 'agentAccommodationView']);
  const dispatch = useAppDispatch();
  const { currentLocale } = useLocale();
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';
  console.log(currentLocale);
  const accommodationFetchParams: AccommodationPublicDetailsParams = {
    cottage: AccommodationSlug,
    siteId: '7BF9E2E4-8567-4EF4-84EE-E38B23B72499',
    includeBasicData: true,
    includeLocation: true,
    includeImages: true,
    includeAttributes: true,
    includeDistances: true,
    locale: currentLanguage,
  };
  const accommodationPublicDataLoadingStatus = useAppSelector(
    selectAccommodationPublicDataLoadingStatus,
  );
  const accommodationPublicData = useAppSelector(
    selectAccommodationPublicDataResult,
  );
  const [images, setImages] = useState<string[]>([]);
  useEffect(() => {
    if (AccommodationSlug) {
      dispatch(getAccommodationPublicData(accommodationFetchParams));
    }
  }, [dispatch, AccommodationSlug]);
  /*
  useEffect(() => {
    if (AccommodationSlug !== '') {
      dispa;
      // Use for fetching and setting cabin data
      setImages([DemoImage1, DemoImage2, DemoImage3]);
    }
  }, [AccommodationId]);
*/
  // console.log(accommodationPublicData);
  function getAccommodationInformationItems(
    attributes?: AttributeData[],
  ): string[] {
    if (!attributes) return [];

    return attributes
      .filter(attr => attr.groupName === 'Information' && attr.value !== null)
      .map(attr => attr.value);
  }

  function getAccommodationAmenities(attributes?: AttributeData[]): Amenity[] {
    if (!attributes) return [];

    return attributes
      .filter(
        attribute =>
          attribute.groupName === 'Equipment' &&
          attribute.value !== null &&
          attribute.value.toLowerCase() !== 'ei' &&
          attribute.value.toLowerCase() !== 'false' &&
          !attribute.value.toLowerCase().includes('ei ') &&
          attribute.value.toLowerCase() !== 'no' &&
          !attribute.value.toLowerCase().includes('no '),
      )
      .map(attribute => {
        if (attribute.value === 'true') {
          return { icon: 'lr', text: attribute.name };
        }
        if (
          attribute.name.toLowerCase() === attribute.value.toLowerCase() ||
          (attribute.typeName === 'MultiSelect' &&
            (attribute.value === 'Kyllä' || attribute.value === 'Yes'))
        ) {
          return { icon: 'lr', text: attribute.name };
        }
        if (attribute.typeName === 'NumericMin') {
          return { icon: 'lr', text: `${attribute.name}: ${attribute.value}` };
        }
        return { icon: 'lr', text: attribute.value };
      });
  }

  function getUniqueDistances(distanceData?: DistanceData[]): DistanceData[] {
    if (!distanceData) return [];

    const uniqueDistances = new Map<string, DistanceData>();

    distanceData.forEach(distance => {
      if (!uniqueDistances.has(distance.name)) {
        uniqueDistances.set(distance.name, distance);
      }
    });

    return Array.from(uniqueDistances.values());
  }

  if (
    accommodationPublicData !== null &&
    accommodationPublicData.basicData !== null
  ) {
    const parsedDistanceData = getUniqueDistances(
      accommodationPublicData.distances,
    );
    return (
      <>
        <h1 className="h3 mb-24">
          {accommodationPublicData.basicData.accommodationNumber}{' '}
          {accommodationPublicData.basicData.accommodationName}
        </h1>

        {accommodationPublicData.images.length > 0 && (
          <Carousel>
            {accommodationPublicData.images.map(image => (
              <Carousel.Item>
                <img
                  alt=""
                  className="d-block object-fit-cover w-100"
                  src={image.imageUrl}
                  style={{ maxHeight: 540 }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        )}

        <div className="my-24">
          <div className="d-block d-md-flex gap-16 align-items-center">
            <Rating
              Rating={accommodationPublicData.basicData.qualityRating}
              CssClass="text-primary"
              Type="customer"
            />
            <span className="d-block">
              {accommodationPublicData.location.municipalityName},{' '}
              {accommodationPublicData.location.regionName},{' '}
              {accommodationPublicData.location.skiResortName}
            </span>
          </div>
          <ul className="d-flex list-unstyled flex-column flex-md-row gap-md-16 mb-0">
            <li className="my-4 my-md-0">
              <span className="bi bi-dot" />
              {accommodationPublicData.basicData.beds}
              {accommodationPublicData.basicData.extraBeds !== 0 &&
                ` + ${accommodationPublicData.basicData.extraBeds}`}{' '}
              {t('agentAccommodationView:persons')}
            </li>
            <li className="my-4 my-md-0">
              <span className="bi bi-dot" />
              {accommodationPublicData.basicData.area} m2
            </li>
            {accommodationPublicData.basicData.bedRooms > 0 && (
              <li className="my-4 my-md-0">
                <span className="bi bi-dot" />
                {accommodationPublicData.basicData.bedRooms}{' '}
                {t('agentAccommodationView:bedRooms')}
              </li>
            )}
            {getAccommodationInformationItems(
              accommodationPublicData.attributes,
            ).map(item => (
              <li className="my-4 my-md-0" key={encodeURI(item)}>
                <span className="bi bi-dot" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="h4">
            {t('agentAccommodationView:accommodationInformation')}
          </h2>
          <p
            dangerouslySetInnerHTML={{
              __html:
                accommodationPublicData.basicData.descriptionStart.replace(
                  /\n/g,
                  '<br />',
                ),
            }}
          />
        </div>

        <hr className="mt-24" />

        <div>
          <h2 className="h4">
            {t('agentAccommodationView:accommodationFeatures')}
          </h2>
          <ul className="list-unstyled row">
            {getAccommodationAmenities(accommodationPublicData.attributes).map(
              (amenity, index) => (
                <li className="col-12 col-md-6" key={amenity.text}>
                  <span className="bi bi-dot me-8" />
                  {amenity.text}
                </li>
              ),
            )}
          </ul>
        </div>

        <hr className="mt-24" />

        <div>
          <h2 className="h4">
            {t('agentAccommodationView:accommodationDistances')}
          </h2>
          <ul className="list-unstyled row">
            {parsedDistanceData.map(distance => (
              <li className="col-12 col-md-6" key={distance.name}>
                <span className="bi bi-dot me-8" />
                {distance.name}: {distance.value}
              </li>
            ))}
          </ul>
        </div>

        <hr className="mt-24" />

        <div>
          <h2 className="h4">
            {t('agentAccommodationView:accommodationAdditionalInformation')}
          </h2>
          <p
            dangerouslySetInnerHTML={{
              __html: accommodationPublicData.basicData.descriptionEnd.replace(
                /\n/g,
                '<br />',
              ),
            }}
          />
        </div>
      </>
    );
  }
  return <Loader IsLoading />;
};

export default AgentAccommodationView;
